import { useState, useEffect, useRef, useCallback } from "react";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import WorkCard from "../components/WorkCard";
import BottomNavbar from "../components/BottomNavbar";
import Header from "../components/Header";
import "./css/Favorites.css";

import noWorkImage from "../assets/images/no_work.png";

export default function Favorites() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [lang] = useState(localStorage.getItem("app_language") || "en");

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;
  const LIMIT = 20;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      noFavorites: "You have not added any work to favorites",
    },
    ar: {
      loading: "جاري التحميل",
      noFavorites: "لم تقم بإضافة أي عمل إلى المفضلة",
    },
  }[lang];

  // Fetch favorites
  const fetchFavorites = async (page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const url = `${USER_BASE_URL}/${VERSION}/work/favorites/?limit=${LIMIT}&page=${page}`;
      const data = await apiFetch(url, "GET", null, {});

      if (data && data.response_code === 200) {
        if (page === 1) {
          setFavorites(data.response || []);
        } else {
          setFavorites((prev) => [...prev, ...(data.response || [])]);
        }
        setHasMorePages((data.response || []).length === LIMIT);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Error fetching favorites:", err);
      showToast(err.message || "Failed to load favorites", "error");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Infinite scroll observer ref
  const observerTarget = useRef(null);

  // Handle infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMorePages) {
      fetchFavorites(currentPage + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, hasMorePages, currentPage]);

  // Infinite scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePages && !loadingMore) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [handleLoadMore, hasMorePages, loadingMore]);

  // Fetch favorites on mount
  useEffect(() => {
    fetchFavorites(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [USER_BASE_URL, VERSION]);

  // Loading screen
  if (loading) {
    return (
      <div className="favorites-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  return (
    <div className="favorites-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Main Content */}
      <div className="favorites-content">
        {favorites.length === 0 ? (
          // No favorites
          <div className="no-content-container">
            <img
              src={noWorkImage}
              alt="No Favorites"
              className="no-content-image"
            />
            <p className="no-content-message">{t.noFavorites}</p>
          </div>
        ) : (
          // Favorites list
          <div className="favorites-list">
            {favorites.map((work) => (
              <WorkCard key={work._id} work={work} lang={lang} />
            ))}
            {/* Infinite scroll trigger */}
            {hasMorePages && (
              <div ref={observerTarget} className="infinite-scroll-trigger">
                {loadingMore && (
                  <div className="loading-more-works">
                    <div className="spinner"></div>
                    <span>{t.loading}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="favorites" lang={lang} />
    </div>
  );
}

